import { useCallback, useEffect } from "react"
import { ApiBalanceParams, ApiGetAggregateQuoteParams, ApiGetBridgeQuoteParams, checkAggregateSupportedToken, getAggregateQuote, getAggregateQuoteMainToken, getAggregateQuoteTokenAddress, getAssetsByChainBase, getBridgeQuote, getEvmBalance, getEvmPrice } from "services/commonServices"
import { useAccount } from "wagmi"
import { useAppDispatch, useAppSelector } from "./redux/hooks"
import { setAggregator, setBalanceEnough, setBalanceEvm, setExtra, setFromAmount, setFromTokenPrice, setGas, setIsMinUsdt, setMinUsdt, setMiniToAmount, setPriceImpact, setRouteSummary, setSlippage, setThirdSwap, setToAmount, setToAmountUsdt, setToTokenPrice } from "./redux/slices/coreInfoSlice"
import { Chain, createPublicClient, formatUnits, http, parseUnits } from "viem"
import { DEF_TOKEN_ADDRESS, SERVICE_FEE_RATE_MOL, SERVICE_FEE_RATE_DEN, DEF_MIN_USDT, getSlippageByToToken, GAS_MINTER, GAS_TO_ADDR } from "utils"
import BigNumber from "bignumber.js";
import useDebounce from "./useDebounce"
import { ChaingeChain, ChaingeToken, GasUsd } from "types/baseType"
import { getDataByEncodeFunctionData } from "utils/viemUtils"
import useIsCrossChain from "./useIsCrossChain"
import { setLoading } from "./redux/slices/appSlice"
import toast from "react-hot-toast"

const useUtils = () => {
    const dispatch = useAppDispatch()
    const { wagmiChains, refreshData, tokens, chains, gasUsdList, brc20 } = useAppSelector((state) => state.app)
    const { fromToken, fromChain, toChain, toToken, balanceEvm, fromAmount, balanceEnough, fromTokenPrice, toTokenPrice } = useAppSelector((state) => state.coreInfo)
    const { isConnected: isConnectedEvm } = useAppSelector((state) => state.wallet)
    const { address: addressEvm, } = useAccount()
    const { decimals = 6, contractAddress = '' } = fromToken || {}
    const { isCrossChain } = useIsCrossChain()

    // 获取EVM余额
    const getBalanceEvm = useCallback(async () => {
        if(!isConnectedEvm || !addressEvm || !fromToken?.contractAddress || !fromChain?.nickName) return
        const params : ApiBalanceParams = {
            chain: fromChain?.nickName,
            address: addressEvm,
            contractAddress: fromToken.contractAddress
        }
        const balance = await getEvmBalance(params)
        dispatch(setBalanceEvm(balance))
    }, [addressEvm, fromChain?.nickName, dispatch, isConnectedEvm, fromToken?.contractAddress])

    useDebounce(() => {
        getBalanceEvm()
    }, 200, [getBalanceEvm, refreshData])

    // 判断evm 的余额是否足够
    useEffect(() => {
      if(!(isConnectedEvm && fromAmount && balanceEvm)) return
      const amountBigInt = parseUnits(fromAmount, decimals)
      const balanceBigInt = parseUnits(balanceEvm, decimals)
      if(amountBigInt > balanceBigInt) {
        dispatch( setBalanceEnough(false))
      } else {
        dispatch( setBalanceEnough(true))
      }
    }, [balanceEvm, fromAmount, isConnectedEvm, decimals, dispatch])

    // 设置最大值
    const setMaxBalance = useCallback(async () => {
        if(!isConnectedEvm) return
        if(contractAddress === DEF_TOKEN_ADDRESS) {
            const curWagmiChain = wagmiChains.find(item => item.id === +fromChain?.network)
            if(curWagmiChain) {
                const publicClient = createPublicClient({
                    chain: curWagmiChain,
                    transport: http()
                }) as any
                let gasPrice : bigint = await publicClient?.getGasPrice()
                let gasLimit = BigInt(21000);
                if(fromChain?.nickName === 'OP') {
                    const double = BigInt(2)
                    gasLimit = gasLimit * double
                }
                const { decimals = 18 } = fromToken || {}
                const gasAmount = gasPrice*gasLimit
                const balanceEvmBigint = parseUnits(balanceEvm, decimals)
                const maxAmount = balanceEvmBigint - gasAmount
                const maxAmountStr = formatUnits(maxAmount, decimals)
                dispatch(setFromAmount(maxAmountStr))
            }
        } else {
            dispatch(setFromAmount(balanceEvm))
        }
    }, [balanceEvm, contractAddress, dispatch, fromChain?.network, fromChain?.nickName, fromToken, isConnectedEvm, wagmiChains])

    // 获取gasFee
    interface GasFeeParams {
        fromAmount: string
        toChain: ChaingeChain
        toToken: ChaingeToken
        wagmiChains: Chain[]
    }
    const getGasFee = useCallback(async (params: GasFeeParams) => {
        const { fromAmount, toChain, toToken, wagmiChains } = params
       try {
            const curWagmiChain = wagmiChains.find(item => item.id === +toChain?.network)
            if(curWagmiChain) {
                const publicClient = createPublicClient({
                    chain: curWagmiChain,
                    transport: http()
                }) as any
 
                let gasPrice : bigint = await publicClient?.getGasPrice()
                let gasLimit = BigInt(0)
                const amount = parseUnits(fromAmount, toToken.decimals)
                if(toToken.contractAddress === DEF_TOKEN_ADDRESS) {
                    // 本币转账
                    gasLimit = await publicClient.estimateGas({ 
                        account: GAS_MINTER,
                        to: GAS_TO_ADDR,
                        value: amount,
                        gasPrice: gasPrice
                    })
                    if(fromChain?.nickName === 'OP') {
                        const double = BigInt(2)
                        gasLimit = gasLimit * double
                    }
                } else {
                    const data = getDataByEncodeFunctionData(amount, GAS_MINTER)
                    gasLimit = await publicClient.estimateGas({ 
                        account: GAS_MINTER,
                        to: GAS_TO_ADDR,
                        gasPrice: gasPrice,
                        data: data
                    })
                }
                // 获取toChain 本币 价格
                const toTokenPrice =  await getEvmPrice({chain: toChain.nickName, symbol: curWagmiChain.nativeCurrency.symbol, contractAddress: DEF_TOKEN_ADDRESS})
                const tempGasFee = gasPrice * gasLimit
                const gasAmount = formatUnits(tempGasFee, 18)
                const gasAmountBigNumber = new BigNumber(gasAmount)
                const tokenPriceBigNumber = new BigNumber(toTokenPrice)
                return gasAmountBigNumber.multipliedBy(tokenPriceBigNumber)
            }
       } catch(error) {
            throw error
       }
    }, [fromChain?.nickName])

    const getGasFeeByCrossChain = useCallback(() => {
        let result = {
            gasUsdt: '0',
            gasAmount: '0',
            gasUsdtBN: new BigNumber(0),
            gasAmountBN: new BigNumber(0),
        }
        if(toToken && toChain && fromTokenPrice && gasUsdList.length) {
            const curGasObj = gasUsdList.find((item: GasUsd) => item.chain === toChain.nickName)
            if(curGasObj) {
                // 本币
                if(toToken.contractAddress.toLowerCase() === DEF_TOKEN_ADDRESS.toLowerCase()) {
                    result.gasUsdt = curGasObj.basecoin+''
                } else {
                    result.gasUsdt = curGasObj.token+''
                }
                const gasUsdtBN = new BigNumber(result.gasUsdt)
                const fromTokenPriceBN = new BigNumber(fromTokenPrice)
                const gasAmountBN = gasUsdtBN.div(fromTokenPriceBN)
                result['gasUsdtBN'] = gasUsdtBN
                result['gasAmountBN'] = gasAmountBN
                result['gasAmount'] = gasAmountBN.toFixed(toToken.decimals)
            }
        }

        return result
    }, [gasUsdList, toToken, toChain, fromTokenPrice])

    // 重制询价参数
    const resetQuoteInfo = useCallback(() => {
        dispatch(setToAmount('0.00'))
        dispatch(setGas(`0.00`))
        dispatch(setAggregator(''))
        dispatch(setRouteSummary(''))
        dispatch(setToAmountUsdt('0.00'))
        dispatch(setSlippage(0))
        dispatch(setMiniToAmount('0.00'))
        dispatch(setIsMinUsdt(false))
        dispatch(setPriceImpact('0.00'))
    }, [dispatch])

    const checkFromAmountMinUsdt = useCallback(async () => {
        const min5UsdtBigNumber = new BigNumber(DEF_MIN_USDT)
        const fromTokenPriceBigNumber = new BigNumber(fromTokenPrice)
        const tempFromAmount = new BigNumber(fromAmount)
        const fromAmountUsdt = tempFromAmount.multipliedBy(fromTokenPriceBigNumber)
        if(!fromAmountUsdt.gte(min5UsdtBigNumber)) {
            dispatch(setIsMinUsdt(true))
        } else {
            dispatch(setIsMinUsdt(false))
        }
    }, [dispatch, fromTokenPrice, fromAmount])

    // 跨链询价 前端计算版
    // useDebounce(async () => {
    //     try {
    //         if(brc20) return
    //         if(!isCrossChain) return
    //         dispatch(setLoading(true))
    //         resetQuoteInfo()
    //         if(!(fromAmount && +fromAmount > 0 && fromToken && fromChain && toToken && toChain)) {
    //             return
    //         }
    //         // 跨链gas费用
    //         const gasObjResult = getGasFeeByCrossChain()

    //         // 计算 to Amount
    //         const fromAmountBN = new BigNumber(fromAmount)
    //         // 当前币的价格
    //         const fromTokenPriceBN = new BigNumber(fromTokenPrice)
    //         // 当前币转换为usdt的价值
    //         const fromAmountUsdt = fromAmountBN.multipliedBy(fromTokenPriceBN)

    //         // 万分之五手续费的计算
    //         // 万五分子
    //         const molBN = new BigNumber(SERVICE_FEE_RATE_MOL)
    //         // 万五分母
    //         const denBN = new BigNumber(SERVICE_FEE_RATE_DEN)
    //         // 万五的比率
    //         const serviceFeeRateBN = molBN.div(denBN)
    //         // 计算万五的数量
    //         const serviceFeeAmountBN = serviceFeeRateBN.multipliedBy(fromAmountBN)

    //         // 计算最小输入数量
    //         const minAmountBN = serviceFeeAmountBN.plus(gasObjResult.gasAmountBN)
            

    //         const tempGasAmountBN = minAmountBN
    //         let tempMinUsdtBN = tempGasAmountBN.multipliedBy(fromTokenPrice)
    //         const tempToAmountBN = fromAmountBN.minus(minAmountBN)
    //         const tempToAmountUsdtBN = tempToAmountBN.multipliedBy(fromTokenPriceBN)

    //         // 默认最小输入价值
    //         const defMinUsdtBN = new BigNumber(DEF_MIN_USDT)
    //         // 如果小于全局的最小输入数量，就用默认的
    //         if(tempMinUsdtBN.lt(defMinUsdtBN)) {
    //             tempMinUsdtBN = defMinUsdtBN
    //         }

    //         // 判断是否符合最小输入数量
    //         if(fromAmountUsdt.lt(tempMinUsdtBN)) {
    //             dispatch(setIsMinUsdt(true))
    //         } else {
    //             dispatch(setIsMinUsdt(false))
    //         }
    //         dispatch(setMinUsdt(tempMinUsdtBN.toFixed(0)))

    //         // 输入数量无法覆盖手续费
    //         if(fromAmountUsdt.lt(tempMinUsdtBN)) {
    //             // 跨链价值 无法 覆盖 gasFee
    //             toast.error("Please try with a larger number.", {
    //                 duration: 3000,
    //                 position: 'top-right',
    //             })
    //             return
    //         }
            
    //         dispatch(setToAmount(tempToAmountBN.toFixed(fromToken?.decimals)))
    //         dispatch(setGas(tempGasAmountBN.toString()))
    //         dispatch(setSlippage(0))
    //         dispatch(setToAmountUsdt(tempToAmountUsdtBN.toFixed(2)))
    //     } catch(error) {
    //         resetQuoteInfo()
    //     } finally {
    //         if(isCrossChain) {
    //             dispatch(setLoading(false))
    //         }
    //     }
    // }, 500, [dispatch, fromAmount, fromChain, fromToken, fromTokenPrice, getGasFeeByCrossChain, isCrossChain, resetQuoteInfo, toChain, toToken, brc20])

    // 跨链询价 接口版
    const fnGetBridgeQuote = useCallback(async() => {
        let errorMsg = ''
        try {
            if(brc20) return
            if(!isCrossChain) return
            dispatch(setLoading(true))
            resetQuoteInfo()
            if(!(fromAmount && +fromAmount > 0 && fromToken && fromChain && toToken && toChain)) {
                return
            }
            const amount = parseUnits(fromAmount, fromToken.decimals).toString()
            const symbol = fromToken.symbol
            const params: ApiGetBridgeQuoteParams = {
                amount: amount,
                symbol: symbol,
                fromChain: fromChain.nickName,
                toChain: toChain.nickName
            }
            const {code = -1, data = {}, msg} = await getBridgeQuote(params)
            if(code === 0) {
                const {gasFee, outAmount, outAmountUsd, serviceFee, price} = data
                const feeBI = BigInt(gasFee)  + BigInt(serviceFee)
                const toAmountBI = BigInt(outAmount) - feeBI
                const fee = formatUnits(feeBI, toToken?.decimals)
                if(toAmountBI <= BigInt(0)) {
                    let minUsdt = DEF_MIN_USDT
                    const feeUsdt = ((+fee) * (+price))
                    if(feeUsdt > minUsdt) {
                        minUsdt = feeUsdt
                    }
                    dispatch(setMinUsdt(minUsdt+''))
                    dispatch(setIsMinUsdt(true))
                    toast.error("Please try with a larger number.", {
                        duration: 3000,
                        position: 'top-right',
                    })
                    return
                } else {
                    dispatch(setIsMinUsdt(false))
                }

                dispatch(setExtra(toAmountBI.toString()))
                dispatch(setToAmount(formatUnits(toAmountBI, toToken?.decimals)))
                dispatch(setGas(fee))
                dispatch(setSlippage(0))
                dispatch(setToAmountUsdt((+outAmountUsd).toFixed(2)))
            } else {
                errorMsg = msg
            }
        } catch(error) {
            dispatch(setFromAmount(''))
            errorMsg = 'Service unavailable, try again later.'
        } finally {
            dispatch(setLoading(false))

            if(errorMsg) {
                toast.error(errorMsg, {
                    duration: 4000,
                    position: 'top-right',
                })
            }
        }
    }, [brc20, dispatch, fromAmount, fromChain, fromToken, isCrossChain, resetQuoteInfo, toChain, toToken])

    useDebounce(() => {
        fnGetBridgeQuote()
    }, 500, [fnGetBridgeQuote])


    const getAtLestAmount = useCallback((symbol: string) => {
        let minAmount = '';
        switch(symbol) {
          case 'ordi':
            minAmount = '3.3'
            break;
          case 'sats':
            minAmount = '476190476'
            break;
          case 'rats':
            minAmount = '1000000'
            break;
          case 'mice':
            minAmount = '15385'
            break;
          case 'EFIL':
            minAmount = '1'
            break;
          case 'TURT':
            minAmount = '9524'
            break;
          case 'BETF':
            minAmount = '3571'
            break;
          case 'cats':
            minAmount = '58824'
            break;
          case 'roup':
            minAmount = '46512'
            break;
          case 'MMSS':
            minAmount = '408'
            break;
          case 'Sqts':
            minAmount = '246913580247'
            break;
          case 'Dovi':
            minAmount = '357'
            break;
          case 'FBTC':
            minAmount = '645'
            break;
          case 'BTCs':
            minAmount = '87'
            break;
          case 'STST':
            minAmount = '1250'
            break;
          case 'piin':
            minAmount = '1538462'
            break;
          case 'csas':
            minAmount = '13333'
            break;
          case 'biis':
            minAmount = '9524'
            break;
          case 'trac':
            minAmount = '55'
            break;
          case 'ONFI':
            minAmount = '273973'
            break;
          case 'xing':
            minAmount = '6897'
            break;
          case 'chax':
            minAmount = '41667'
            break;
          case 'drac':
            minAmount = '34483'
            break;
          case 'bank':
            minAmount = '4000'
            break;
          case 'GHSY':
            minAmount = '417'
            break;
          case 'foox':
            minAmount = '52632'
            break;
          case 'BNSx':
            minAmount = '488'
            break;
          case 'RDEX':
            minAmount = '1053'
            break;
          case '$BMP':
            minAmount = '20000'
            break;
          case '.com':
            minAmount = '93'
            break;
          case 'PECL':
            minAmount = '1666667'
            break;
          case '🏠':
            minAmount = '6897'
            break;
          default:
            minAmount = ''
        }
        return minAmount
    }, [])

    // brc20 询价
    useDebounce(async () => {
        if(!brc20) return
        resetQuoteInfo()
        if(fromChain && toChain && fromToken && toToken && fromAmount && +fromAmount > 0) {
            const symbol = fromToken?.symbol
            const atLestAmount = getAtLestAmount(symbol)
            if(atLestAmount > fromAmount) {
                dispatch(setMinUsdt(atLestAmount))
                dispatch(setIsMinUsdt(true))
                toast.error("Please try with a larger number.", {
                    duration: 3000,
                    position: 'top-right',
                })
                return
            } else {
                dispatch(setIsMinUsdt(false))
            }
            
            dispatch(setToAmount(fromAmount))
        }
    }, 500, [fromChain, toChain, fromToken, toToken, fromAmount, dispatch, resetQuoteInfo, getAtLestAmount])

    // 交易询价
    const fnCheckAggregateSupportedToken = useCallback(async () => {
        if((fromChain && fromToken && toChain && toToken && fromAmount && +fromAmount > 0)) {
            const params = {
                fromChain,
                toChain,
                fromToken,
                toToken
            }
            const isQuoteMain = await checkAggregateSupportedToken(params)
            return isQuoteMain
        }
    }, [fromChain, fromToken, toChain, toToken, fromAmount])

    const formatQuoteResult = useCallback((result: any, coreParams: any ) => {
        const { amountOut, gasUSD, feeUsdBN, gasDexUsdBN, lastUsdBN } = result
        const { fromChain, toChain, fromToken, toToken, toTokenPrice, curToken, fromAmountUsdBN } = coreParams
        if(fromChain?.network === toChain?.network) {
            // 直接模式，不扣除手续费
            // toToken价格
            // const toTokenPriceBN = new BigNumber(toTokenPrice)
            // const gasFeeTotalBN = new BigNumber(gasUSD)
            // 总的手续费数量
            // const gasFeeTotalAmountBN = gasFeeTotalBN.div(toTokenPriceBN)

            result['toAmount'] = formatUnits(BigInt(amountOut), curToken.decimals)
            result['gasAmount'] = '0.00'
        } else {
            // result['toAmount'] = formatUnits(amountOut, curToken.decimals)
            const amountOutStr = formatUnits(BigInt(amountOut), curToken.decimals)
            const amountOutBN = new BigNumber(amountOutStr)

            // toToken价格
            const toTokenPriceBN = new BigNumber(toTokenPrice)
            // 总的手续费
            let gasFeeTotalBN = feeUsdBN.plus(gasDexUsdBN)
            // 总的手续费数量
            const gasFeeTotalAmountBN = gasFeeTotalBN.div(toTokenPriceBN)

            // 默认最小输入价值
            const defMinUsdtBN = new BigNumber(DEF_MIN_USDT)
            // 如果小于全局的最小输入数量，就用默认的
            if(gasFeeTotalBN.lt(defMinUsdtBN)) {
                gasFeeTotalBN = defMinUsdtBN
            }
            dispatch(setMinUsdt(gasFeeTotalBN.toFixed(0)))

            if(fromAmountUsdBN.lt(gasFeeTotalBN)) {
                // 跨链价值 无法 覆盖 gasFee
                dispatch(setIsMinUsdt(true))
                toast.error("Please try with a larger number.", {
                    duration: 3000,
                    position: 'top-right',
                })
                return ''
            } else {
                dispatch(setIsMinUsdt(false))
                const toAmountBN = amountOutBN.minus(gasFeeTotalAmountBN)
                result['toAmount'] = toAmountBN.toFixed(curToken.decimals)
                result['amountOutUSD'] = toAmountBN.multipliedBy(toTokenPrice).toString()
                result['gasAmount'] = gasFeeTotalAmountBN.toFixed(curToken.decimals)

                // result['toAmount'] = lastUsdBN.div(toTokenPrice).toFixed(curToken.decimals)
                // result['amountOutUSD'] = lastUsdBN.toString()
                // result['gasAmount'] = gasFeeTotalAmountBN.toFixed(curToken.decimals)
            }
        }
        result['slippage'] = getSlippageByToToken(fromToken.symbol, toToken.symbol)
        return result
    }, [dispatch])

    /**
     * 获取主流币询价结果
     */
    const fnGetAggregateQuoteMainToken = useCallback(async (supportChains: string[]) => {
        if(fromAmount && fromToken && toToken && toChain && fromChain && +fromAmount > 0) {
            // const amount = parseUnits(fromAmount, fromToken?.decimals).toString()
            const tempChainIDs = []
            if(supportChains.includes(fromChain?.network)) {
                tempChainIDs.push(fromChain?.network)
            }
            if(supportChains.includes(toChain.network)) {
                tempChainIDs.push(toChain?.network)
            }
            let chainIDs = ''
            if(tempChainIDs.length > 0) {
                chainIDs = tempChainIDs.join(",")
            }
            
            const amount = fromAmount
            const params = {
                fromSymbol: fromToken.symbol,
                toSymbol: toToken.symbol,
                fromAmount: amount,
                chainIDs
            }

            const isBaseCoin = toToken.contractAddress.toLowerCase() === DEF_TOKEN_ADDRESS.toLowerCase()
            const result = await getAggregateQuoteMainToken(params, fromChain?.network, toChain?.network, gasUsdList, isBaseCoin)
            if(result) {
                const { chainId } = result
                const curChain = chains.find(item => item.network === chainId)
                if(curChain) {
                    const chainName = curChain.nickName
                    
                    // 判断资产是否在同一条链
                    if(toChain.nickName === chainName) {
                        const hasToken = await getAssetsByChainBase(chainName, fromToken?.symbol)
                        if(!hasToken) {
                            return 'error'
                        }
                    }

                    const curToken = await getAssetsByChainBase(chainName, toToken?.symbol)
                    if(curToken) {
                        const fromAmountBN = new BigNumber(fromAmount)
                        const fromAmountUsdBN = fromAmountBN.multipliedBy(new BigNumber(fromTokenPrice))
                        return formatQuoteResult(result, {
                            fromChain,
                            toChain,
                            fromToken,
                            toToken,
                            curToken,
                            toTokenPrice,
                            fromAmountUsdBN
                        })
                    }
                    return 'error'
                }
                return 'error'
            }
            return 'error'
        } else {
            return '' 
        }
    }, [fromAmount, fromToken, toToken, toChain, fromChain, gasUsdList, chains, fromTokenPrice, formatQuoteResult, toTokenPrice])


    /**
     * 获取from链的询价结果
     */
    const fnGetAggregateQuoteTokenAddressByFrom = useCallback(async() => {
        if(fromChain && toChain && fromToken && toToken && fromAmount && +fromAmount > 0) {
            const fromContractAddress = fromToken.contractAddress
            const fromDecimals = fromToken.decimals
            let toContractAddress = ''
            let toDecimals = -1
            const toTokenAll = tokens.find(item => item.symbol === toToken.symbol)
            if(toTokenAll) {
                const contractAddressList =  toTokenAll.contracts
                const curToTokenAddress =  contractAddressList[fromChain.nickName]
                if(curToTokenAddress) {
                    toContractAddress = curToTokenAddress['address']
                    toDecimals = curToTokenAddress['decimals']
                }
            }
            if(fromContractAddress.toLowerCase() === toContractAddress.toLocaleLowerCase()) {
                return ''
            }
            if(fromContractAddress && toContractAddress && fromDecimals > 0 && toDecimals >0) {
                const amount = parseUnits(fromAmount, fromDecimals)
                // const amount = fromAmount
                const params = {
                    chain: fromChain?.nickName,
                    fromToken: fromContractAddress,
                    toToken: toContractAddress,
                    fromDecimals: fromDecimals,
                    toDecimals: toDecimals,
                    fromAmount: amount.toString()
                }
                const isBaseCoin = toContractAddress.toLowerCase() === DEF_TOKEN_ADDRESS.toLowerCase()
                const result = await getAggregateQuoteTokenAddress(params, fromChain?.network, toChain?.network, gasUsdList, 'from', isBaseCoin)
                if(result) {
                    const { amountOut, gasUSD, chainId, feeUsdBN, gasDexUsdBN } = result
                    const curChain = chains.find(item => item.network === chainId)
                    if(curChain) {
                        const chainName = curChain.nickName
                        
                        // 判断资产是否在同一条链
                        if(toChain.nickName === chainName) {
                            const hasToken = await getAssetsByChainBase(chainName, fromToken?.symbol)
                            if(!hasToken) {
                                return 'error'
                            }
                        }

                        const curToken = await getAssetsByChainBase(chainName,  toToken?.symbol)
                        if(curToken) {
                            const fromAmountBN = new BigNumber(fromAmount)
                            const fromAmountUsdBN = fromAmountBN.multipliedBy(new BigNumber(fromTokenPrice))
                            return formatQuoteResult(result, {
                                fromChain,
                                toChain,
                                fromToken,
                                toToken,
                                curToken,
                                toTokenPrice,
                                fromAmountUsdBN
                            })
                        }
                        return 'error'
                    }
                    return 'error'
                }
                return 'error'
            }
            return 'error'
        } else {
            return ''
        }
    }, [fromChain, toChain, fromToken, toToken, fromAmount, tokens, gasUsdList, chains, fromTokenPrice, formatQuoteResult, toTokenPrice])

    /**
     * 获取to链的询价结果
     */
    const fnGetAggregateQuoteTokenAddressByTo = useCallback(async() => {
        if(fromChain && toChain && fromToken && toToken && fromAmount && +fromAmount > 0) {
            const toContractAddress = toToken.contractAddress
            const toDecimals = toToken.decimals
            let fromContractAddress = ''
            let fromDecimals = -1
            const fromTokenAll = tokens.find(item => item.symbol === fromToken.symbol)
            if(fromTokenAll) {
                const contractAddressList =  fromTokenAll.contracts
                const curToTokenAddress =  contractAddressList[toChain.nickName]
                if(curToTokenAddress) {
                    fromContractAddress = curToTokenAddress['address']
                    fromDecimals = curToTokenAddress['decimals']
                }
            }
            if(fromContractAddress.toLowerCase() === toContractAddress.toLocaleLowerCase()) {
                return ''
            }
            if(fromContractAddress && toContractAddress && fromDecimals > 0 && toDecimals >0) {
                const amount = parseUnits(fromAmount, fromDecimals)
                // const amount = fromAmount
                const params = {
                    chain: toChain?.nickName,
                    fromToken: fromContractAddress,
                    toToken: toContractAddress,
                    fromDecimals: fromDecimals,
                    toDecimals: toDecimals,
                    fromAmount: amount.toString()
                }
                const isBaseCoin = toContractAddress.toLowerCase() === DEF_TOKEN_ADDRESS.toLowerCase()
                const result = await getAggregateQuoteTokenAddress(params, fromChain?.network, toChain?.network, gasUsdList, 'to', isBaseCoin)
                if(result) {
                    const { amountOut, gasUSD, chainId, feeUsdBN, gasDexUsdBN } = result
                    const curChain = chains.find(item => item.network === chainId)
                    if(curChain) {
                        const chainName = curChain.nickName

                        // 判断资产是否在同一条链
                        if(toChain.nickName === chainName) {
                            const hasToken = await getAssetsByChainBase(chainName, fromToken?.symbol)
                            if(!hasToken) {
                                return 'error'
                            }
                        }

                        const curToken = await getAssetsByChainBase(chainName,  toToken?.symbol)
                        if(curToken) {
                            const fromAmountBN = new BigNumber(fromAmount)
                            const fromAmountUsdBN = fromAmountBN.multipliedBy(new BigNumber(fromTokenPrice))
                            return formatQuoteResult(result, {
                                fromChain,
                                toChain,
                                fromToken,
                                toToken,
                                curToken,
                                toTokenPrice,
                                fromAmountUsdBN
                            })
                        }
                        return 'error'
                    }
                    return 'error'
                }
                return 'error'
            }
            return 'error'
        } else {
            return ''
        }
    }, [chains, formatQuoteResult, fromAmount, fromChain, fromToken, fromTokenPrice, gasUsdList, toChain, toToken, toTokenPrice, tokens])

    /**
     * 设置询价结果
     */
    const setAggregateQuoteVal = useCallback((tempResult: any) => {
        const { aggregator, toAmount, routeSummary="", amountOutUSD="0.00", slippage, diffPercent=0.00, gasAmount="0.00" } = tempResult
        dispatch(setToAmount(toAmount))
        dispatch(setGas(`${gasAmount}`))
        dispatch(setAggregator(aggregator))
        dispatch(setRouteSummary(routeSummary))
        dispatch(setToAmountUsdt(amountOutUSD))
        dispatch(setSlippage(slippage))
        dispatch(setPriceImpact(Math.abs(diffPercent).toFixed(2)))

        const tempMimiToAmount = toAmount - toAmount * (slippage / 100)
        dispatch(setMiniToAmount(tempMimiToAmount.toString()))

        console.log('aggregator:', tempResult.aggregator)
        console.log('chianId:', tempResult.chainId)
        console.log('gasDexUsd:', tempResult.gasDexUsdBN.toString())
        console.log('feeUsd:', tempResult.feeUsdBN.toString())
        console.log('lastUsd:', tempResult.lastUsdBN.toString())
        console.log('gasAmount:', tempResult.gasAmount)
    }, [dispatch])


    const printParams = useCallback(() => {
        if(fromAmount && +fromAmount > 0 && fromChain && fromToken && toChain && toToken) {
            const params = {
                sourceCerts: {
                    fromChain: fromChain?.nickName,
                    fromIndex: fromToken?.index+'',
                    fromAmount: parseUnits(fromAmount, fromToken?.decimals).toString()
                },
                orderType: isCrossChain ? '1' : '2',
                toChain: toChain?.nickName,
                toIndex: toToken?.index+''
            }
            console.log('params:', JSON.stringify(params))
        }
    }, [fromAmount, fromChain, fromToken, isCrossChain, toChain, toToken])

    // 交易询价 前端版本
    // useDebounce(async () => {
    //     let errorMsg = ''
    //     try {
    //         if(brc20) return
    //         if(isCrossChain) return
    //         resetQuoteInfo()
    //         if(+fromAmount <= 0) return
    //         await checkFromAmountMinUsdt()
    //         dispatch(setLoading(true))
    //         const supportTokenResult = await fnCheckAggregateSupportedToken()
    //         if(!supportTokenResult) return
    //         if(supportTokenResult && supportTokenResult.isMainToken) {
    //             const result = await fnGetAggregateQuoteMainToken(supportTokenResult.supportChains)
    //             if(result && result !== 'error') {
    //                 setAggregateQuoteVal(result)
    //                 return
    //             }
    //             if(result === 'error') {
    //                 errorMsg = 'Unsupported pair'
    //                 dispatch(setFromAmount(''))
    //             }
    //         } else {
    //             const resultFrom = await fnGetAggregateQuoteTokenAddressByFrom()
    //             const resultTo = await fnGetAggregateQuoteTokenAddressByTo()
    //             if((resultFrom && resultFrom !== 'error') && (resultTo && resultTo !== 'error')) {
    //                 let tempResult =  {}
    //                 if(resultFrom.atLeastBN.gte(resultTo.amountOutUSD)) {
    //                     tempResult = resultFrom
    //                 } else {
    //                     tempResult = resultTo
    //                 }
    //                 setAggregateQuoteVal(tempResult)
    //                 return
    //             } else if(resultFrom && resultFrom !== 'error') {
    //                 setAggregateQuoteVal(resultFrom)
    //                 return
    //             } else if(resultTo && resultTo !== 'error') {
    //                 setAggregateQuoteVal(resultTo)
    //                 return
    //             }

    //             if(resultFrom === 'error' && resultTo === 'error') {
    //                 dispatch(setFromAmount(''))
    //                 errorMsg = 'Unsupported pair'
    //             }
    //         }
    //     } catch(error) {
    //         errorMsg = 'Service unavailable, try again later.'
    //         dispatch(setFromAmount(''))
    //     } finally {
    //         printParams()
    //         if(!isCrossChain) {
    //             dispatch(setLoading(false))
    //         }
    //         if(errorMsg) {
    //             toast.error(errorMsg, {
    //                 duration: 4000,
    //                 position: 'top-right',
    //             })
    //         }
    //     }
    // }, 500, [brc20, isCrossChain, fromAmount, fnCheckAggregateSupportedToken, fnGetAggregateQuoteMainToken, fnGetAggregateQuoteTokenAddressByFrom, fnGetAggregateQuoteTokenAddressByTo, resetQuoteInfo, dispatch, checkFromAmountMinUsdt])

    // 交易询价 接口版本
    const fnGetAggregateQuote = useCallback(async() => {
        let errorMsg = ''
        try {
            if(brc20) return
            if(isCrossChain) return
            resetQuoteInfo()
            if(!(fromAmount && +fromAmount > 0 && fromToken && fromChain && toToken && toChain)) {
                return
            }
            dispatch(setLoading(true))
            const amount = parseUnits(fromAmount, fromToken.decimals).toString()
            const fromTokenAddress = fromToken.contractAddress
            const fromDecimal = fromToken.decimals+''

            const toTokenAddress = toToken.contractAddress
            const toDecimal = toToken.decimals+''
            const params: ApiGetAggregateQuoteParams = {
                fromAmount: amount,
                fromTokenAddress: fromTokenAddress,
                fromDecimal: fromDecimal,
                fromChain: fromChain.nickName,
                toTokenAddress: toTokenAddress,
                toDecimal: toDecimal,
                toChain: toChain.nickName
            }
            const {code = -1, data = {}, msg} = await getAggregateQuote(params)
            if(code === 0) {
                const { aggregator, gasFee, minOutAmount, chain, outAmount, outAmountUsd, priceImpact, serviceFee, routeSummary = '' } = data
                const curChain = chains.find(item => item.network === chain)
                if(!curChain) {
                    dispatch(setFromAmount(''))
                    if(msg.includes('no aggregate router')) {
                        errorMsg = 'Unsupported pair'
                    }
                    return
                }

                const curToken = await getAssetsByChainBase(curChain.nickName,  toToken?.symbol)
                const { decimals } = curToken
                const feeBI = BigInt(gasFee) + BigInt(serviceFee)
                const slippage = getSlippageByToToken(fromToken.symbol, toToken.symbol)
                const toAmountBI = BigInt(outAmount) - feeBI
                const fee = formatUnits(feeBI, decimals)
                const toAmount = formatUnits(toAmountBI, decimals)

                if(toAmountBI <= BigInt(0)) {
                    let minUsdt = DEF_MIN_USDT
                    const feeUsdt = (+fee * (+toTokenPrice))
                    if( feeUsdt > minUsdt) {
                        minUsdt = feeUsdt
                    }
                    dispatch(setMinUsdt(minUsdt+''))
                    dispatch(setIsMinUsdt(true))
                    toast.error("Please try with a larger number.", {
                        duration: 3000,
                        position: 'top-right',
                    })
                    return
                } else {
                    dispatch(setIsMinUsdt(false))
                }

                // check is thirdSwap
                if(fromChain.network !== chain && toChain.network !== chain) {
                    dispatch(setThirdSwap(true))
                } else {
                    dispatch(setThirdSwap(false))
                }
                
                dispatch(setExtra(toAmountBI.toString()))
                dispatch(setToAmount(toAmount))
                dispatch(setGas(fee))
                dispatch(setAggregator(aggregator))
                dispatch(setRouteSummary(routeSummary))
                dispatch(setToAmountUsdt(outAmountUsd))
                dispatch(setPriceImpact(Math.abs(priceImpact).toFixed(2)))
                dispatch(setSlippage(slippage))
        
                const tempMimiToAmount = (+toAmount) - (+toAmount) * (slippage / 100)
                dispatch(setMiniToAmount(tempMimiToAmount.toString()))
            } else {
                dispatch(setFromAmount(''))
                if(msg.includes('no aggregate router')) {
                    errorMsg = 'Unsupported pair'
                } else {
                    errorMsg = msg
                }
            }

        } catch(error) {
            dispatch(setFromAmount(''))
            errorMsg = 'Service unavailable, try again later.'
        } finally {
            if(!isCrossChain) {
                dispatch(setLoading(false))
            }
            if(errorMsg) {
                toast.error(errorMsg, {
                    duration: 4000,
                    position: 'top-right',
                })
            }
        }
    }, [brc20, chains, dispatch, fromAmount, fromChain, fromToken, isCrossChain, resetQuoteInfo, toChain, toToken, toTokenPrice])

    useDebounce(() => {
        fnGetAggregateQuote()
    }, 500, [fnGetAggregateQuote])
    
    // 获取价格
    const getFromTokenPrice = useCallback(async () => {
        if(fromToken && fromChain) {
            const tokenPrice =  await getEvmPrice({chain: fromChain?.nickName, symbol: fromToken?.symbol, contractAddress: fromToken?.contractAddress})
            dispatch(setFromTokenPrice(tokenPrice))
        }
    }, [fromToken, fromChain, dispatch])

    const getToTokenPrice = useCallback(async () => {
        if(toToken && toChain) {
            const tokenPrice =  await getEvmPrice({chain: toChain?.nickName, symbol: toToken?.symbol, contractAddress: toToken?.contractAddress})
            dispatch(setToTokenPrice(tokenPrice))
        }
    }, [toToken, toChain, dispatch])
    useDebounce(() => {
        getToTokenPrice()
        getFromTokenPrice()
    }, 300, [getFromTokenPrice, getToTokenPrice])

    return {balanceEnough, isConnectedEvm, toToken, toChain,  getBalanceEvm, setMaxBalance}
}

export default useUtils